import type { DocPage } from '../types'
import { media } from '../media'

export const settings: DocPage = {
  id: 'settings',
  title: 'Settings',
  description:
    'Configure coordinate conversion, format defaults, global logging levels and formats, and view About information.',
  category: 'Workspace',
  sections: [
    { id: 'coords', title: 'Coordinate conversion' },
    { id: 'format-defaults', title: 'Format defaults' },
    { id: 'logging', title: 'Logging' },
    { id: 'log-levels', title: 'Log levels and formats' },
    { id: 'about', title: 'About' },
    { id: 'entitlements', title: 'Access restricted messages' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'coords',
      text: 'Coordinate conversion',
    },
    {
      type: 'paragraph',
      text: 'Open Transfer → Transfer Settings → Coordinate Conversion (or the equivalent menu entry Coordinate Conversion). This sets a global xy-coordinate transform used for coordinate conversion during transfer.',
    },
    {
      type: 'table',
      headers: ['Field / action', 'Description'],
      rows: [
        ['X', 'Global X offset applied during conversion.'],
        ['Y', 'Global Y offset applied during conversion.'],
        ['Change coordinates / Change', 'Enter edit mode (requires change entitlement).'],
        ['Save', 'Persist the new X/Y values.'],
        ['Cancel', 'Discard edits and close without saving.'],
      ],
    },
    {
      type: 'paragraph',
      text: 'Transfer Review shows the active Coordinate conversions (X/Y) and a Change shortcut when you are allowed to edit. Feature gates: view-coordinate-conversion and change-coordinate-conversion.',
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'What it is not',
      text: 'Coordinate conversion here is the global XY offset (property key xy-coordinate). It is not a full reprojection UI. Per-format CRS still matters — for example CAD Override EPSG / EPSG Code on the connection form, and Text/CSV EPSG (geometry CRS).',
    },
    {
      type: 'heading',
      level: 2,
      id: 'format-defaults',
      text: 'Format defaults',
    },
    {
      type: 'paragraph',
      text: 'Formats → Format Configuration / Default Settings stores per-format defaults. PostGIS and AutoCAD have defaults pathways in the product; other formats may show as coming soon. Separately, Mark as default on a Model Configuration pre-fills New connections for that format — see Model configurations.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'logging',
      text: 'Logging',
    },
    {
      type: 'list',
      items: [
        'Logger → Global Log Settings — three accordions: Log Level, Log Format, and Language (feature-gated by change-log-settings).',
        'Logger → Log Configurations — template/component listing for advanced logging setup.',
        'Bottom workspace logger tabs: LOGS, EVENTS, ERRORS.',
        'Transfer History can Download Logs / View Logs for a completed run when entitled.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'log-levels',
      text: 'Log levels and formats',
    },
    {
      type: 'paragraph',
      text: 'Open Logger → Global Log Settings. Expand Log Level to change Current Log Level. The panel shows a short description for the selected level and Last Updated when the setting was saved.',
    },
    {
      type: 'figure',
      src: media.globalLogSettingsLevel,
      alt: 'Global Log Settings — Log Level accordion',
      caption:
        'Figure: Global Log Settings — Log Level expanded with Current Log Level (for example DETAILED Level 2).',
      size: 'compact',
    },
    {
      type: 'table',
      headers: ['Log level', 'Code', 'Typical detail'],
      rows: [
        ['NO LOG', '0', 'Suppress logger output.'],
        ['STANDARD', '1', 'Normal operational messages for everyday transfers.'],
        ['DETAILED', '2', 'Richer progress and diagnostic messages.'],
        ['DEBUG', '3', 'Maximum verbosity for support and deep diagnosis.'],
      ],
    },
    {
      type: 'paragraph',
      text: 'Expand Log Format to choose how each line is structured (Simple, Detailed, Debug, and related options). The panel shows a Format Template preview and the Enabled Components used in that template (for example timestamp, level, category, message_code, message_text, meta).',
    },
    {
      type: 'figure',
      src: media.globalLogSettingsFormat,
      alt: 'Global Log Settings — Log Format accordion',
      caption:
        'Figure: Global Log Settings — Log Format with Current Log Format, Format Template, and Enabled Components.',
      size: 'compact',
    },
    {
      type: 'paragraph',
      text: 'Expand Language to set Current Log Language (for example German (DE) or English). Log messages and logging-related interface text follow the selected language.',
    },
    {
      type: 'figure',
      src: media.globalLogSettingsLanguage,
      alt: 'Global Log Settings — Language accordion',
      caption:
        'Figure: Global Log Settings — Language with Current Log Language selector.',
      size: 'compact',
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'When to raise the level',
      text: 'Leave STANDARD for production use. Switch to DETAILED or DEBUG temporarily when a transfer fails with unclear ERRORS, then return to STANDARD so the logger stays readable.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'about',
      text: 'About',
    },
    {
      type: 'paragraph',
      text: 'Open About from the menu bar to see product version, copyright, and Legal Notices for SEPM X-Translator. Use the version string (for example 1.0.13) when contacting support.',
    },
    {
      type: 'figure',
      src: media.about,
      alt: 'About SEPM X-Translator dialog',
      caption:
        'Figure: About SEPM X-Translator — application name, version, copyright, and Legal Notices.',
      size: 'compact',
    },
    {
      type: 'heading',
      level: 2,
      id: 'entitlements',
      text: 'Access restricted messages',
    },
    {
      type: 'table',
      headers: ['Setting', 'Message when blocked'],
      rows: [
        [
          'View coordinate conversion',
          'You don\'t have access to view the coordinates conversion, contact the admin',
        ],
        [
          'Change coordinate conversion',
          'You don\'t have access to change the coordinates conversion, contact the admin',
        ],
        [
          'Change log settings',
          'You don\'t have access to change log settings, contact the admin',
        ],
        ['Generic gate', 'Access restricted'],
      ],
    },
  ],
}
